import { select } from '@angular-redux/store';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Component, ElementRef, forwardRef, Inject, QueryList, ViewChild, ViewChildren } from '@angular/core';
import * as moment from 'moment/moment';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    currencySymbols,
    currencyUtcSymbols,
    LicenseFrequency,
    LicenseFrequencyType,
    LicensePackageOptions,
    LicensePackageType,
    LicenseStatus
} from 'sharedFeatures/enums';
import { FeedbackPopupComponent } from 'sharedFeatures/feedback-popup';
import { infoPopUpComponent } from "sharedFeatures/infoPopUp";
import { Account, AuthData, CommonApp, Subscription } from 'sharedFeatures/sharedModels';
import { BillingService, RedirectService } from "sharedFeatures/sharedServices";
import { AuthUtils, NavigationUtils, Utils } from 'sharedFeatures/utils';
import { DialogCreator } from "sharedFeatures/window";
import { ComboComponent, ItemData } from "sharedGuiSmall/components";
import { CurrencyGeoData } from 'sharedGuiSmall/utils/currencyData';
import { SharedGuiSmallModule } from '../../../../sharedGuiSmall/sharedGuiSmall.module';
import { ScrollComponent } from '../scroll/scrolldiv.component';
import { UpgradeComponent } from '../upgrade/upgrade.component';
import { SwitchLinkComponent } from './switch-link.component';
import { UpgradeWin } from './upgrade-win.component';
import { TranslateModule } from '@ngx-translate/core';

declare let handleAngularHttpError: any;

@Component({
	selector: 'upgrade-win-body',
	templateUrl: './upgrade-win-body.component.html',
	styleUrls: ['./upgrade-win-body.component.less', "./forms_style.less"],
	standalone: true,
	imports: [SharedGuiSmallModule, ScrollComponent, UpgradeComponent, TranslateModule],
	providers: [BillingService, RedirectService]
})
export class UpgradeWinBodyComponent {

	licensePackageType = LicensePackageType;
	LicensePackageOptions = LicensePackageOptions;
	LicenseFrequency = LicenseFrequency;

	links: Array<SwitchLinkComponent> = new Array<SwitchLinkComponent>();

	@ViewChild(ScrollComponent)
	private scrollpane!: ScrollComponent;

	@ViewChildren(infoPopUpComponent)
	infoPopupItems: QueryList<infoPopUpComponent> | undefined = undefined;

	@ViewChild('plansCountCombo')
	plansCountCombo: ComboComponent | undefined;

	initialSubscriptionType: LicenseFrequencyType = 'None';
	subscriptionRef: string = '';
	subscriptionType = this.initialSubscriptionType;
	subscriptionTypeChanged: boolean = false;
	subscriptionCompaniesId: string = '';

	userGeo = Utils.getNavigatorLanguage().replace(/-/g, '_');

	userSubscriptions: Array<Subscription> = [];

	brixxSinglePlansCountItems: Array<ItemData> = [
		{ id: '1', value: '1 Plan Included', elementId: 1, disabled: false, },
		{ id: '2', value: '2 Plans', elementId: 2, disabled: false, },
		{ id: '3', value: '3 Plans', elementId: 3, disabled: false, },
		{ id: '4', value: '4 Plans', elementId: 4, disabled: false, },
		{ id: '5', value: '5 Plans', elementId: 5, disabled: false, },
		{ id: '6', value: '6 Plans', elementId: 6, disabled: false, },
		{ id: '7', value: '7 Plans', elementId: 7, disabled: false, },
		{ id: '8', value: '8 Plans', elementId: 8, disabled: false, },
		{ id: '9', value: '9 Plans', elementId: 9, disabled: false, },
		{ id: '10', value: '10 Plans', elementId: 10, disabled: false, },
		{ id: '11', value: '15 Plans', elementId: 15, disabled: false, },
		{ id: '12', value: '20 Plans ', elementId: 20, disabled: false, },
		{ id: '13', value: '30 Plans', elementId: 30, disabled: false, },
		{ id: '14', value: '40 Plans', elementId: 40, disabled: false, },
		{ id: '15', value: '50 Plans', elementId: 50, disabled: false, },
	];

	selectedPlansCount: string = '5';

	private serverDateTime: moment.Moment | undefined;
	remainingDays: number = 0;
	trialTimeText: string = '';

	currentCurrencyId: number = 1;
	currentCurSymbol: string = '$';
	selectedCurCurrency: string = 'USD';
	currentFrequency: string = LicenseFrequency.Monthly;
	currentExtraPlansPrice: string = '7';

	currentPriceEssentials: string = '22';
	currentPriceProfessional: string = '44';
	currentPriceEnterprise: string = '66';
	currentPriceSingle: string = '33';

	prices: Record<string, string[][]> | undefined= undefined;

	currencySymbols = currencyUtcSymbols;

	isButtonDisabled: boolean = false;

	isCurrentLicenseAnnually: boolean = false;
	isMonthly: boolean = true;

	isTrial: boolean = false;
	isFree: boolean = false;
	isBrixxSingle: boolean = false;

	isPaidMonthly: boolean = true;
	isPaidAnnually: boolean = false;

	switchToggleFrequency: boolean = false;

	brixxUrl: string = 'https://brixx.com/';
	textCurrentUsing: string = $localize`YOU ARE CURRENTLY USING`;
	txtBtnSwitchOption: string = $localize`Switch to Annual Billing`;
	currentlyPayText = $localize`YOU CURRENTLY PAY`;
	wouldPayText = $localize`YOU WOULD PAY`;

	paidAnnually: string = $localize`paid annually`;
	paidMonthly: string = $localize`paid monthly`;
	dayText = $localize`day`;
	daysText = $localize`days`;

	
	payOptionText: string = this.currentlyPayText;

	initialPackageType = LicensePackageType.None;
	packageType = this.initialPackageType;
	productVersion: string = '';	

	initialLicenseStatus: string = '';
	initialPriceOptionCodes: string[] = [];
	initialLicenseEndDate: string = '';

	monthlySubscriptionChosen: boolean = false;

	actionType: string = '';
	billing: string = '';

	isNewUser: boolean = false;
	isAlternativePricingAvailable: boolean = false;
	isForceBuyLink: boolean = false;
	isFromManageBillingPanel: boolean = false;
	isFromUpgradeLink: boolean = false;
	isLoading: boolean = true;

	selectPackageFont: string = 'p1Font';
	singlePlansCount: string = '1';
	useNewTestProduct: boolean = false;

	// Unsubscribe event
	private storeUnsubscribe = new Subject<void>();

	@select(['account', 'accountData'])
	readonly accountData$!: Observable<Account>;
	private accountData: Account = new Account();

	@select(['account', 'authData'])
	readonly authData$!: Observable<AuthData>;
	authData: AuthData = new AuthData();

	@select(['account', 'UIState'])
	readonly UIState$!: Observable<any>;
	private UIState: any | undefined;

	/*************** main_app store **********************/
	@select(['appData', 'appComponent'])
	readonly appComponent$!: Observable<any>;
	private appComponent: any | undefined;
	/*****************************************************/

	/*************** account_app store *******************/
	@select(['accountAppData', 'accountAppComponent'])
	readonly accountAppComponent$!: Observable<any>;
	private accountAppComponent: any | undefined;
	/*****************************************************/

	activeSwitchLink: SwitchLinkComponent | undefined;
	switchLinkValue: LicenseFrequencyType | undefined;

	licensePlanId: number = 1;

	constructor(@Inject(forwardRef(() => UpgradeWin)) public parent: UpgradeWin,
		private billingService: BillingService,
		private redirectService: RedirectService,
		private http: HttpClient) {
		this.selectedPlansCount = '1';
		this.useNewTestProduct = (String)(window['useNewTestProduct']).toLowerCase() === 'true';
	}

	ngOnInit() {
		// Subscribe on the redux store
		this.subscribeReduxStore();
		this.getServerTime();
	}

	ngAfterViewInit(): void {
		this.scrollpane?.onLayoutChange();
		
		if (this.authData.licenseStatus) {			
			this.getCurrentLicensePackage(this.authData.licenseStatus, this.authData.priceOptionCodes);
		}
	}

	ngOnDestroy() {
		// Unsubscribe from the redux store
		this.storeUnsubscribe.next();
		this.storeUnsubscribe.complete();
	}

	// Subscribe on the redux store
	private subscribeReduxStore(): void {
		this.accountData$?.pipe(
			takeUntil(this.storeUnsubscribe))
			?.subscribe((x: Account) => {
				this.accountData = x;
			});

		this.authData$?.pipe(
			takeUntil(this.storeUnsubscribe))
			?.subscribe((x: AuthData) => {
				this.authData = x;

				this.isAlternativePricingAvailable = AuthUtils.isBetaFeatureActive(this.authData, 'AlternativePricing');
				this.selectPackageFont = 'h3Font';

				this.initialSubscriptionType = AuthUtils.getLicenseFrequency(this.authData.priceOptionCodes);
				this.initialPackageType = AuthUtils.getLicensePackageType(this.authData.priceOptionCodes);
				if (this.authData.licenseStatus)
					this.initialLicenseStatus = this.authData.licenseStatus;
				if (this.authData.licenseEndDate)
					this.initialLicenseEndDate = this.authData.licenseEndDate;

				this.initialPriceOptionCodes = this.authData.priceOptionCodes;				
				let currencyId = currencyUtcSymbols.find(c => c.value == this.authData.subscriptionCurrencyCode)?.id;

				let initialProductCodes = this.getCurrentPackageCodes(this.initialPackageType);

				if (currencyId != undefined){
					this.billingService.GetPricesTable(initialProductCodes.productCode).then(data => {
						this.prices = JSON.parse(data);
						this.selectCurrency();
						this.getGeoBasedCurrencyFoundAndTrial();
						this.getExtraPlansPrice();
					})
				}

				if (this.initialLicenseStatus === LicenseStatus.Trial) {
					this.initialPackageType = LicensePackageType.None;
					this.initialSubscriptionType = 'None';
				}

				if (localStorage.getItem('isFromUpgradeLink') == 'true') {
					localStorage.setItem('isFromUpgradeLink', '');
					this.loadSubscriptions();
					this.isFromUpgradeLink = true;
					return;
				}
				else if (localStorage.getItem('isFromManageBillingPanel') !== 'true')
					this.loadSubscriptions();

				this.subscriptionType = this.initialSubscriptionType;
				this.packageType = this.initialPackageType;

				if (this.subscriptionType === LicenseFrequency.None)
					this.subscriptionType = 'Annually';

				if ((this.initialLicenseStatus === LicenseStatus.Trial ||
					this.initialPackageType === LicensePackageType.None ||
					this.initialPackageType === LicensePackageType.BrixxFree)
					&& !this.isFromManageBillingPanel)
					this.isNewUser = true;

				this.setActionType(this.initialPackageType);
				this.onChangeSubscriptionType(this.subscriptionType);

				if (this.isForceBuyLink)
					this.actionType = '';

			});

		this.UIState$?.pipe(
			takeUntil(this.storeUnsubscribe))
			?.subscribe((x: any) => this.UIState = x);

		this.appComponent$?.pipe(
			takeUntil(this.storeUnsubscribe))
			?.subscribe((x: any) => this.appComponent = x);

		this.accountAppComponent$?.pipe(
			takeUntil(this.storeUnsubscribe))
			?.subscribe((x: any) => { this.accountAppComponent = x; });
		this.switchToggleFrequency = this.initialSubscriptionType === 'Annually';
	}
	
	get textArticles(){
		if(this.initialPackageType && this.LicensePackageOptions){
			const firstWordOfPackageType:string = this.LicensePackageOptions.get(this.initialPackageType)!.OptionName.toLowerCase().charAt(0);
			const vowels = ['a', 'e', 'i', 'o', 'u', 'y'];
			const isVowels = vowels.includes(firstWordOfPackageType);

			return isVowels ? 'an' : 'a';
		}
	}
	
	get upgradeTxtCountOptions(){
		let txtCount = '';
		
		switch (this.initialPackageType){
			case LicensePackageType.BrixxFree:
				txtCount = 'three';
				break;
		}
		
		return txtCount;
	}

	getCurrentLicensePackage(licenseStatus: string, priceOptionCodes: string[]):void{
		this.isTrial = licenseStatus == LicenseStatus.Trial;
		this.isFree = this.initialPackageType == LicensePackageType.BrixxFree;
		this.isBrixxSingle = this.initialPackageType == LicensePackageType.BrixxSingle;

		this.getAccountPricePlan(priceOptionCodes);

		this.isNewUser = ((licenseStatus === LicenseStatus.Trial ||
			this.initialPackageType === LicensePackageType.None ||
			this.initialPackageType === LicensePackageType.BrixxFree)
			&& !this.isFromManageBillingPanel);			
	}


	getAccountPricePlan(priceOptionCodes: string[]){
		this.currentFrequency = AuthUtils.getLicenseFrequency(priceOptionCodes);
		this.currentFrequency = this.isTrial || this.isFree ? LicenseFrequency.Annually : this.currentFrequency;

		if(this.currentFrequency == LicenseFrequency.Monthly){
			this.isPaidMonthly = true;
			this.isPaidAnnually = false;
		}
		if(this.currentFrequency == LicenseFrequency.Annually){
			this.isPaidMonthly = false;
			this.isPaidAnnually = true;
			this.isCurrentLicenseAnnually = true;
			this.payOptionText = this.currentlyPayText;
		}

	}

	public get PaymentPackage():boolean{
		let isPaymentPackage:boolean = false;

		if(this.initialPackageType == this.licensePackageType.BrixxFree)
		{
			isPaymentPackage = true;
		}

		return isPaymentPackage;
	}

	getCurrentPackageCodes(packageType: string) { 
		let packageOptionUniqueCode = '';
		LicensePackageOptions.forEach((value, key) => {
			if (key && packageType.indexOf(key) !== -1)
				packageOptionUniqueCode = value.UniqueCode;
		});

		let productCode = this.useNewTestProduct ? NavigationUtils.singleTestProductCode : NavigationUtils.productCode;

		if (packageType == LicensePackageType.BrixxSingle && parseInt(this.singlePlansCount)) {
			packageOptionUniqueCode = LicensePackageType.BrixxSingle + this.singlePlansCount;		
		}
		
		return {
			productCode, packageOptionUniqueCode
		};
	}

	// New payment method
	public openPayment(packageType: string): void {				

		let packageCodes = this.getCurrentPackageCodes(packageType);
		
		let isNotTrialOrFree = this.initialLicenseStatus !== LicenseStatus.Trial && this.initialLicenseStatus !== LicenseStatus.Free;
		if (this.actionType === 'Upgrade' && (isNotTrialOrFree || this.isFromManageBillingPanel || this.isFromUpgradeLink)) {
			NavigationUtils.openChangePaymentPage(
				this.accountData,
				packageCodes.packageOptionUniqueCode,
				this.currentFrequency,
				this.selectedCurCurrency,
				this.redirectService.getAccountUrl(),
				packageCodes.productCode,
				this.productVersion,
				this.subscriptionRef,
				// use all user companies when upgrade
				this.subscriptionCompaniesId
			);
		} else {
			NavigationUtils.openPaymentPage(
				this.accountData,
				packageCodes.packageOptionUniqueCode,
				this.currentFrequency,
				this.selectedCurCurrency,
				this.redirectService.getAccountUrl(),
				packageCodes.productCode,
				this.productVersion,
				this.subscriptionRef,
				// use all user companies when buy
				this.subscriptionCompaniesId
			);
		}
	}

	changeSinglePlansCount(value: string) { 
		let planItem = this.brixxSinglePlansCountItems.find(p => p.id == value);

		if (planItem) {
			let planItemCount = planItem.elementId;

			this.singlePlansCount = planItemCount ? planItemCount.toString() : this.singlePlansCount;

			if (this.prices !== undefined) {

				this.currentPriceSingle = this.isPaidMonthly
					? this.prices[this.selectedCurCurrency][0][parseInt(value) - 1]
					: this.prices[this.selectedCurCurrency][1][parseInt(value) - 1];

				this.getExtraPlansPrice();

			}
		}
	}

	//Timer
	private getServerTime(){
		const response = this.http.post<string>(window.balancerApiUrl + 'DataForms/GetServerDateTime', null);
		lastValueFrom(response)
			.then((res: string) => {
				const data = JSON.parse(res);
				this.serverDateTime = moment(data.serverDateTime);
				this.updateTrialTime();
			})
			.catch((error: HttpErrorResponse) => {
				console.error('GetServerDateTime error', error);

				handleAngularHttpError(error);
			});
	}

	private updateTrialTime(): void {
		if (!this.authData || !this.serverDateTime) return;

		const endDate: moment.Moment = moment(this.initialLicenseEndDate);

		const hours: number = endDate.diff(this.serverDateTime, 'hours');

		this.remainingDays = Math.floor(hours / 24) + 1;

		this.trialTimeText = this.remainingDays == 1 ? `${this.remainingDays} ${this.dayText}` : `${this.remainingDays} ${this.daysText}`;
	}


	selectCurrency(): void{
		this.selectedCurCurrency = this.getCurrencyUtcSymbol(this.currentCurrencyId);
		this.currentCurSymbol = this.getCurrencySymbol(this.currentCurrencyId);
		this.selectPrice(this.currentCurrencyId);

		for(let i = 0; i < currencyUtcSymbols.length; i++){
			currencyUtcSymbols[i].disabled = currencyUtcSymbols[i].id == this.currentCurrencyId;
		}

		let planItem = this.brixxSinglePlansCountItems.find(p => p.elementId == parseInt(this.singlePlansCount));
		if (planItem)
			this.changeSinglePlansCount(planItem.id);
	}
	

	selectPrice(currencyId: number) {
		const currency = currencyUtcSymbols.find(cur => cur.id == currencyId)!.value;		
		
		let planItemId = this.brixxSinglePlansCountItems.find(p => p.elementId == parseInt(this.singlePlansCount));
		if (planItemId)
			this.changeSinglePlansCount(planItemId.id);		
		
	}

	selectFrequency(event: any) {
		this.licensePlanId = event.target.checked ? 1 : 0;

		switch (this.licensePlanId) {
			case 0:
				this.isPaidMonthly = true;
				this.isPaidAnnually = false;
				this.currentFrequency = LicenseFrequency.Monthly;
				this.payOptionText = this.currentlyPayText;
				break;
			case 1:
				this.isPaidMonthly = false;
				this.isPaidAnnually = true;
				this.currentFrequency = LicenseFrequency.Annually;
				this.payOptionText = this.wouldPayText;
				if (this.isCurrentLicenseAnnually){
					this.payOptionText = this.currentlyPayText;
				}
				break;
		}

		this.selectPrice(this.currentCurrencyId);
		this.setActionType(this.initialPackageType);
	}

	private switchToggle(): void{
		this.isMonthly = !this.isMonthly;
	}

	selectFrequencyForEnterprise(){
		this.switchToggleFrequency = !this.switchToggleFrequency;
		this.switchToggle();
		const licensePlanId = this.isMonthly ? 0 : 1;
		
		switch (licensePlanId){
			case 0:
				this.isPaidMonthly = true;
				this.isPaidAnnually = false;
				this.currentFrequency = LicenseFrequency.Monthly;
				this.payOptionText = this.currentlyPayText;
				break;
			case 1:
				this.isPaidMonthly = false;
				this.isPaidAnnually = true;
				this.currentFrequency = LicenseFrequency.Annually;
				this.payOptionText = this.wouldPayText;
				if (this.isCurrentLicenseAnnually){
					this.payOptionText = this.currentlyPayText;
				}
				break;
		}

		this.selectPrice(this.currentCurrencyId)
	}

	initSubscriptionParams(
		subscriptionRef: string,
		subscriptionType: LicenseFrequencyType,
		packageType: string,
		productVersion: string = '2.0',
		subscriptionCompaniesId: string = '',
		price: string = '',
		licenseStatus: string = '',
		priceOptionCodes: string[] = [],
		licenseEndDate: string = ''
	) {
		this.subscriptionRef = subscriptionRef;
		this.initialSubscriptionType = subscriptionType;
		this.initialPackageType = packageType;
		
		if (licenseStatus != '')
			this.initialLicenseStatus = licenseStatus;
		
		if (priceOptionCodes.length > 0)
			this.initialPriceOptionCodes = priceOptionCodes;

		if (licenseEndDate != '')
			this.initialLicenseEndDate = licenseEndDate;

		if (this.initialLicenseStatus === LicenseStatus.Trial) {
			this.initialPackageType = LicensePackageType.None;
			this.initialSubscriptionType = 'None';
		}

		this.subscriptionCompaniesId = subscriptionCompaniesId;

		this.subscriptionType = this.initialSubscriptionType;
		this.packageType = this.initialPackageType;
		this.productVersion = productVersion;

		if (this.subscriptionType === LicenseFrequency.None)
			this.subscriptionType = 'Annually';

		this.setActionType(this.initialPackageType);
		this.onChangeSubscriptionType(this.subscriptionType);

		this.selectedPlansCount = '1';

		this.setCurrentPrice(price);
		this.getCurrentLicensePackage(this.initialLicenseStatus, this.initialPriceOptionCodes);
		this.getServerTime();
	}

	onClose() {
		this.parent.close();

		// Once user has submitted feedback
		let feedbackPopupSubmitted: boolean = false;
		if (this.UIState && this.UIState.FeedbackPopup)
			feedbackPopupSubmitted = this.UIState.FeedbackPopup.submitted === 'true';

		if (this.authData &&
			(this.packageType == LicensePackageType.BrixxFree || this.initialLicenseStatus === LicenseStatus.Trial) &&
			!feedbackPopupSubmitted) {
			this.showFeedbackPopup();
		}
	}

	setCurrentPrice(price?: string) {
		if (price) {
			this.selectedCurCurrency = price.split(' ')[1];			
		} else{
			this.selectedCurCurrency = CurrencyGeoData.getGeoBasedCurrency(this.userGeo);
		}

		this.currentCurSymbol = NavigationUtils.currencies.get(this.selectedCurCurrency) ?? '$';
	}

	loadSubscriptions(): void {
		localStorage.setItem('isFromManageBillingPanel', '');
		this.billingService.UserSubscriptionPromise.then((subscriptions: Array<Subscription> | undefined) => {
			
			if (subscriptions)
				this.userSubscriptions = subscriptions.filter(x => x.SubscriptionStatus != LicenseStatus.Free);

			if (this.userSubscriptions && this.userSubscriptions.length > 0) {

				let price = this.userSubscriptions.find(x => x.SubscriptionReference === this.authData.subscriptionID)?.Fee ?? undefined;

				this.setCurrentPrice(price);
			}
			else
				this.selectedCurCurrency = CurrencyGeoData.getGeoBasedCurrency(this.userGeo);
		});
	}	

	getGeoBasedCurrencyFoundAndTrial(){
		if ((this.authData && this.initialLicenseStatus === LicenseStatus.Trial) || this.initialPackageType === LicensePackageType.BrixxFree) {
			this.selectedCurCurrency = CurrencyGeoData.getGeoBasedCurrency(this.userGeo);
			const currencyId = currencyUtcSymbols.find(currency => currency.value === this.selectedCurCurrency)?.id;
			if (currencyId === undefined)
				return;
			this.selectPrice(currencyId);
			this.selectCurrency();
		}
	}

	
	getDialogCreator(): DialogCreator {
        return this.getApp();
	}

    getApp(): CommonApp {
        if (this.appComponent)
            return this.appComponent;
        else if (this.accountAppComponent)
            return this.accountAppComponent;

        throw new Error('Architecture error.');
    }

	private showFeedbackPopup(): void {
		let popup: FeedbackPopupComponent;

		if (this.parent.getDialogCreator().getWinByClass(FeedbackPopupComponent) !== undefined) 
            return
		popup = <FeedbackPopupComponent>this.parent.getDialogCreator().createDialog(FeedbackPopupComponent);

		popup.appElem = this.parent.dialogCreator;
		popup.setSize(popup.width, window.innerHeight > document.body.scrollHeight ? window.innerHeight : document.body.scrollHeight);
		popup.show(window.innerWidth - popup.width, 0);
	}

	elementEnabled(element: string): boolean {
		
		if (this.initialSubscriptionType === LicenseFrequency.Annually && (element.toLowerCase().indexOf('monthly') !== -1))
			return this.monthlyOptionEnabled(element);

		return true;
	}

	monthlyOptionEnabled(element: string): boolean {

		let monthlyOptionType = element.split(' ')[0];

		return monthlyOptionType === this.initialPackageType;
	}

	setActionType(packageType: LicensePackageType): void {
		if (this.isForceBuyLink) {
			this.actionType = '';
			return;
		}

		if (!(this.initialPackageType === LicensePackageType.None || this.initialPackageType === LicensePackageType.BrixxFree || this.initialLicenseStatus === LicenseStatus.Trial))
			this.setUpgrade();
	}

	setUpgrade(): void {
		this.actionType = 'Upgrade';
		this.billing = 'billing';
	}

	onChangeSubscriptionType(val: string): void {
		this.subscriptionTypeChanged = val !== this.initialSubscriptionType;

		this.switchLinkValue = LicenseFrequency[val as keyof LicenseFrequency];
		this.subscriptionType = LicenseFrequency[val as keyof LicenseFrequency];

		this.monthlySubscriptionChosen = this.subscriptionType === LicenseFrequency.Monthly;
	}

	clearActive(): void {
		if (this.activeSwitchLink) this.activeSwitchLink.setClicked(false);
		this.activeSwitchLink = undefined;
	}

	public addLink(link: SwitchLinkComponent): void {
		this.links.push(link);
	}

	public closeOtherInfoPanels(pFieldID: string): void {
		// Close all other infoPopups within the component
		this.infoPopupItems?.forEach(x => {
			if (x.pFieldID !== pFieldID) x.close();
		});
	}

	public goToVideoTour(licenseNumber: number){
		let typePackage = '';

		switch (licenseNumber) {
			case 0:
				typePackage = 'brixx-foundations';
				break;
			case 1:
				typePackage = 'brixx-essentials';
				break;
			case 2:
				typePackage = 'brixx-professional';
				break;
			case 3:
				typePackage = 'brixx-enterprise';
				break;
		}

		window.open(this.brixxUrl + typePackage);
	}

	public isMostPopularVisible(element: string): boolean {
		return element !== this.initialPackageType;
	}

	private getCurrencyUtcSymbol(currencyId: number): string {
		const symbol: string | undefined = currencyUtcSymbols.find(currency => currency.id == currencyId)?.value;
		if (symbol !== undefined)
			return symbol;

		console.error('getCurrencyUtcSymbol() error: could not find currency symbol in currencyUtcSymbols, currencyId=', currencyId, ', previous value was returned');
		return this.currentCurSymbol;
	}

	private getCurrencySymbol(currencyId: number): string {
		const symbol: string | undefined = currencySymbols.find(currency => currency.id == currencyId)?.value;
		if (symbol !== undefined)
			return symbol;

		console.error('getCurrencySymbol() error: could not find currency symbol in currencySymbols, currencyId=', currencyId, ', previous value was returned');
		return this.currentCurSymbol;
	}

	public getBilledPrice() : string {
		const priceSingle = parseFloat(this.currentPriceSingle);
		const billedPrice = this.isPaidAnnually ? priceSingle * 12 : priceSingle

		const formattedPrice = billedPrice.toFixed(2);

		return formattedPrice.endsWith('.00') ? billedPrice.toFixed(0) : formattedPrice;
	}

	public getExtraPlansPrice() {
		if (!this.prices) {
			return;
		}

		const priceIndex = this.isPaidMonthly ? 0 : 1;
		const firstPrice = parseFloat(this.prices[this.selectedCurCurrency][priceIndex][0]);
		const secondPrice = parseFloat(this.prices[this.selectedCurCurrency][priceIndex][1]);
		const extraPlansPrice = secondPrice - firstPrice;

		const formattedPrice = extraPlansPrice.toFixed(2);
		this.currentExtraPlansPrice = formattedPrice.endsWith('.00') ? extraPlansPrice.toFixed(0) : formattedPrice;
	}
}
